<script setup>
import { computed, defineProps, ref, watch } from "vue";
import { alert } from "@/svgs.js";
import HyphenInput from "../../base/HyphenInput.vue";
import HyphenSelect from "../../base/HyphenSelect.vue";
import { getForms } from "@/api.js";

const props = defineProps({
  form: {
    type: Object,
    default: null,
  },
});

const editForm = ref({
  label: "",
  placeholder: "",
  description: "",
  required: false,
  source: "",
  choice: "",
  data: "",
});

const dataSource = [
  {
    label: "Form",
    value: "form",
  },
  {
    label: "Contacts",
    value: "contacts",
  },
  // {
  //   label: "External",
  //   value: "external",
  // },
];

const emit = defineEmits({
  edit(val) {
    return val;
  },
  remove(val) {
    return val;
  },
  cancelEdit(val) {
    return val;
  },
});

const forms = ref([]);
const contacts = ref([
  {
    label: "Customers",
    value: "customer",
  },
  {
    label: "Vendors",
    value: "vendor",
  },
  {
    label: "Team",
    value: "staff",
  },
]);

const externalSource = ref([
  {
    label: "Paystack",
    value: "paystack",
  },
  {
    label: "Flutterwave",
    value: "flutterwave",
  },
  {
    label: "Bloc",
    value: "bloc",
  },
  {
    label: "Mono",
    value: "mono",
  },
  {
    label: "Stripe",
    value: "stripe",
  },
]);

const contactOptions = ref([
  {
    label: "First name",
    value: "firstname",
  },
  {
    label: "Last name",
    value: "lastname",
  },
  {
    label: "Email",
    value: "email",
  },
  {
    label: "Phone",
    value: "phone",
  },
  {
    label: "Customer ID",
    value: "hypn_id",
  },
  {
    label: "Organization name",
    value: "company",
  },
]);

watch(
  () => props.form,
  (val) => {
    if (val) {
      // Directly assign props.form to editForm.value
      editForm.value = {
        label: val.label,
        ...val.attributes, // Copy attributes
        value: val.value,
        required: val.required.toString(),
      };
    }
  },
  { immediate: true }
);

// );

// Simplify the computed property canApply
const canApply = computed(
  () =>
    !!editForm.value.label && // Check if label is not empty
    editForm.value.data
);

const apply = () => {
  const data = {
    id: props.form.id,
    type: props.form.type,
    label: editForm.value.label,
    key: key.value,
    required: editForm.value.required === "true" ? true : false,
    locked: props.form.locked || false,
    attributes: {
      placeholder: editForm.value.placeholder,
      description: editForm.value.description,
      editable: props.form.attributes.editable ?? true,
      source: editForm.value.source,
      choice: editForm.value.choice,
      data: editForm.value.data,
    },
  };
  emit("edit", data);
  // console.log(JSON.stringify(data, null, 2));
};

const key = computed(() => {
  // convert string to snake case
  return editForm.value.label
    .toLowerCase()
    .replace(/ /g, "_")
    .replace(/[^\w-]+/g, "");
});

const cancel = () => {
  if (!props.form.label) {
    emit("remove", props.form);
  } else {
    emit("cancelEdit", props.form);
  }
};

const choiceOptions = computed(() => {
  if (editForm.value.source === "form") {
    return forms.value.map((form) => {
      return {
        label: form.label,
        value: form.value,
      };
    });
  } else if (editForm.value.source === "contacts") {
    return contacts.value;
  } else {
    return externalSource.value;
  }
});

const dataOptions = computed(() => {
  if (editForm.value.source === "form") {
    return forms.value
      .find((form) => form.value === editForm.value.choice)
      ?.fields.map((field) => {
        return {
          label: field.label,
          value: field.key,
        };
      })
      .filter((field) => !field.label.includes("content-label"));
  } else if (
    editForm.value.source === "contacts" &&
    (editForm.value.choice === "customer" || editForm.value.choice === "vendor")
  ) {
    return contactOptions.value;
  } else if (
    editForm.value.source === "contacts" &&
    editForm.value.choice === "staff"
  ) {
    return [
      {
        label: "First name",
        value: "firstname",
      },
      {
        label: "Last name",
        value: "lastname",
      },
      {
        label: "Phone",
        value: "phone",
      },
      {
        label: "Company Name",
        value: "companyname",
      },
      {
        label: "Staff ID",
        value: "id",
      },
    ];
  } else {
    return [
      {
        label: "Paystack",
        value: "paystack",
      },
      {
        label: "Flutterwave",
        value: "flutterwave",
      },
      {
        label: "Bloc",
        value: "bloc",
      },
      {
        label: "Mono",
        value: "mono",
      },
      {
        label: "Stripe",
        value: "stripe",
      },
    ];
  }
});
const isFetchingFields = ref(false);
const fetchForms = async () => {
  try {
    isFetchingFields.value = true;
    const response = await getForms();
    forms.value = response.data.map((form) => {
      return {
        label: form.form_title,
        value: form.hypn_id,
        fields: form.field_names,
      };
    });
  } catch (error) {
    console.log(error);
  } finally {
    isFetchingFields.value = false;
  }
};

watch(
  () => editForm.value.source,
  (val) => {
    if (val === "form") {
      fetchForms();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="editForm" class="hyphen-data-dash">
    <h2>Field Settings</h2>
    <div class="hyphen-data-dash__header">
      <div class="text-hint">
        <span v-html="alert"></span><span>Data Field</span>
      </div>
      <p>
        A single selection field, allows anyone to select from dynamic data that
        exist within this platform or an external data source
      </p>
    </div>
    <div class="hyphen-data-dash__content">
      <hyphen-input
        v-model="editForm.label"
        id="dash-input-label"
        placeholder="Select Label Text"
        label="Label"
        description="Label of select you want to create"
      />

      <hyphen-input
        v-model="editForm.placeholder"
        id="dash-input-placeholder"
        placeholder="Enter placeholder text"
        label="Placeholder (optional)"
        description="Placeholder of input you want to create"
      />

      <hyphen-input
        v-model="editForm.description"
        id="dash-input-description"
        placeholder="Enter description text"
        label="Description (optional)"
        description="Description of input you want to create"
      />

      <hyphen-select
        v-model="editForm.source"
        @changed="() => (editForm.choice = '')"
        label="Data source"
        placeholder="Select data source"
        description="This is the data source"
        option-label="label"
        option-value="value"
        :options="dataSource"
      />

      <hyphen-select
        v-if="editForm.source"
        v-model="editForm.choice"
        :is-loading="isFetchingFields"
        @changed="() => (editForm.data = '')"
        :label="`Choose ${
          editForm.source === 'form'
            ? 'form'
            : editForm.source === 'contacts'
            ? 'contact type'
            : 'external source'
        }`"
        placeholder="Select"
        description="Make your choice"
        option-label="label"
        option-value="value"
        :options="choiceOptions"
      />

      <hyphen-select
        v-if="editForm.choice"
        v-model="editForm.data"
        label="Use this data"
        placeholder="Select data"
        description="Select data field"
        option-label="label"
        option-value="value"
        :options="dataOptions"
      />

      <hyphen-select
        v-model="editForm.required"
        label="Required"
        placeholder="Select a type"
        description="Is this field required?"
        option-label="label"
        option-value="value"
        :options="[
          {
            label: 'Yes',
            value: 'true',
          },
          {
            label: 'No',
            value: 'false',
          },
        ]"
      />
    </div>
    <div class="hyphen-data-dash__action">
      <button @click="cancel" class="cancel">Cancel</button>
      <button
        @click="apply"
        v-if="canApply"
        class="apply"
        :class="{
          'apply--disabled': !canApply,
        }"
      >
        Apply
      </button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap");

.hyphen-data-dash {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    color: var(--hyphen-Blue, #19283d);
    font-family: Inter;
    font-size: 17px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    /* 175% */
  }

  &__header {
    padding: 20px;
    margin: 20px 0px;
    border-radius: 4px;
    background: #f8f9fa;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.03);

    .text-hint {
      display: flex;
      align-items: center;
      gap: 10px;

      & > span:nth-child(2) {
        color: #000;
        font-family: Inter;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 22px;
        margin-bottom: 3px;
      }
    }

    p {
      color: var(--body-text, rgba(25, 40, 61, 0.8));
      font-family: Inter;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      /* 183.333% */
    }
  }

  &__content {
    flex: 1;
    overflow: auto;
    gap: 30px;
    display: flex;
    flex-direction: column;
    padding: 10px;
  }

  &__content::-webkit-scrollbar {
    width: 8px;
    border-radius: 10px;
  }

  &__content::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
  }

  &__content::-webkit-scrollbar-thumb {
    background-color: var(--hyphen-Blue, #19283d);
    border-radius: 10px;
  }

  &__action {
    box-sizing: border-box;
    display: flex;
    gap: 20px;
    padding: 10px 0px;
    margin-bottom: 40px;
    z-index: 99;

    button {
      flex: 1;

      padding: 15px 30px;
      border-radius: 4px;
      border: none;

      font-size: 16px;
      font-weight: 500;
      cursor: pointer;

      &.cancel {
        border-radius: 4px;
        background: var(--secondary-button, #f4f5f6);
      }

      &.apply {
        border-radius: 4px;
        color: #fff;
        background: var(--hyphen-Blue, #19283d);
      }
    }
  }

  &__label {
    color: rgba(25, 40, 61, 0.8);
    font-size: 14px;
    font-style: normal;
    line-height: normal;
    font-weight: 500;
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    border: 1.8px solid #d9dee1;
    border-radius: 5px;
    padding: 12px 0px;
    background-color: transparent;

    .input-target {
      flex: 1;
      min-width: auto;
      outline-style: none;
      border-radius: inherit;
      background-color: inherit;
      border: none;
      color: #666;
      font-size: 15px;
    }

    .input-suffix {
      margin-right: 20px;
    }
  }
}

.hyphen-input__input-wrapper {
  display: flex;
  align-items: center;
  border: 1.8px solid #d9dee1;
  border-radius: 5px;
  padding: 12px 0px;
  background-color: transparent;

  .input-target {
    flex: 1;
    min-width: auto;
    outline-style: none;
    border-radius: inherit;
    background-color: inherit;
    border: none;
    color: #666;
    font-size: 15px;
    padding-left: 10px;
  }

  .input-suffix {
    margin-right: 20px;
    display: flex;
    align-items: center;
    width: 16px;
    height: 12px;
    padding-left: 10px;
    padding-left: 10px;

    .icon-x {
      font-size: 24px;
      color: rgba(217, 222, 225, 1);
      cursor: pointer;
      font-family: "inter";
      font-weight: 200;

      &:hover {
        color: red;
      }
    }
  }
}

.add-button {
  text-decoration: underline;
  color: #5e6978;
  font-size: 12px;
  font-family: "Inter";
  font-weight: 400;
  cursor: pointer;
  padding-top: 8px;
}

.input-target:focus::placeholder {
  color: transparent;
}
</style>
